import React, { useEffect, useRef, useState } from "react";
import { Modal, Input, Button, Space, message, Row, Col, Typography, Tooltip, Upload, List } from "antd";
import { PaperClipOutlined, UploadOutlined, DeleteOutlined } from "@ant-design/icons";
import { postReq } from "../api";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import dayjs from "dayjs";

const { Text, Title } = Typography;

const PLACEHOLDERS = [
  { label: "First Name", value: "{{firstName}}" },
  { label: "Last Name", value: "{{lastName}}" },
  { label: "Email", value: "{{email}}" },
  { label: "Company", value: "{{companyName}}" },
];

export default function CreateTemplateModal({ open, onClose, template, onSuccess }) {
  const isEdit = !!template;
  const editorRef = useRef(null);
  const quillInstance = useRef(null);
  const lastBodyRangeRef = useRef(null);
  const subjectInputRef = useRef(null);
  const subjectSelectionRef = useRef({ start: 0, end: 0 });

  // States for the inputs
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [bodyContent, setBodyContent] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [existingAttachments, setExistingAttachments] = useState([]); // already-saved files: { fileName, originalName, size }
  const [newAttachments, setNewAttachments] = useState([]); // freshly picked File objects, not yet uploaded
  const [saving, setSaving] = useState(false);

  // Initialize/Reset form on open/template change
  useEffect(() => {
    if (open) {
      setSubmitted(false);
      if (template) {
        setName(template.name);
        setSubject(template.subject);
        setBodyContent(template.body);
        setExistingAttachments(template.attachments || []);
      } else {
        setName(`New Template - ${dayjs().format("DD MMM YYYY")}`);
        setSubject("");
        setBodyContent("");
        setExistingAttachments([]);
      }
      setNewAttachments([]);

      // Initialize Quill after the modal renders the DOM
      const timer = setTimeout(() => {
        if (editorRef.current) {
          editorRef.current.innerHTML = "";

          quillInstance.current = new Quill(editorRef.current, {
            theme: "snow",
            placeholder: "Compose your email body here...",
            modules: {
              toolbar: [
                ['bold', 'italic', 'underline'],
                [{ 'color': [] }],
                [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                ['link', 'clean']
              ]
            }
          });

          // Set initial content
          if (template) {
            quillInstance.current.root.innerHTML = template.body;
          }

          // Handle text changes
          quillInstance.current.on('text-change', () => {
            setBodyContent(quillInstance.current.root.innerHTML);
          });

          // Remember the last place the caret was, since clicking a
          // shortcut button blurs the editor and clears its selection
          quillInstance.current.on('selection-change', (range) => {
            if (range) lastBodyRangeRef.current = range;
          });
        }
      }, 100);

      return () => clearTimeout(timer);
    } else {
      quillInstance.current = null;
      lastBodyRangeRef.current = null;
    }
  }, [open, template]);

  // Insert variable helper
  const handleInsertPlaceholder = (placeholder, isSubject = false) => {
    if (isSubject) {
      const { start, end } = subjectSelectionRef.current;
      setSubject((prev) => {
        const s = Number.isInteger(start) ? start : prev.length;
        const e = Number.isInteger(end) ? end : prev.length;
        const newValue = prev.slice(0, s) + placeholder + prev.slice(e);
        const caretPos = s + placeholder.length;

        requestAnimationFrame(() => {
          const el = subjectInputRef.current?.input;
          if (el) {
            el.focus();
            el.setSelectionRange(caretPos, caretPos);
          }
          subjectSelectionRef.current = { start: caretPos, end: caretPos };
        });

        return newValue;
      });
    } else if (quillInstance.current) {
      const range = quillInstance.current.getSelection() || lastBodyRangeRef.current;
      const index = range ? range.index : quillInstance.current.getLength();
      quillInstance.current.insertText(index, placeholder, "user");
      const newPos = index + placeholder.length;
      quillInstance.current.setSelection(newPos, 0, "user");
      lastBodyRangeRef.current = { index: newPos, length: 0 };
    }
  };

  // Track caret/selection inside the Subject input so shortcut clicks
  // (which blur the input) still know where to insert
  const captureSubjectSelection = (e) => {
    const el = e.target;
    subjectSelectionRef.current = {
      start: el.selectionStart,
      end: el.selectionEnd,
    };
  };

  const handleSubmit = async () => {
    setSubmitted(true);
    // Validation
    if (!name.trim() || !subject.trim() || !bodyContent.trim() || bodyContent === "<p><br></p>") {
      message.error("All fields (Title, Subject, and Body) are required");
      return;
    }

    const formData = new FormData();
    formData.append("name", name);
    formData.append("subject", subject);
    formData.append("body", bodyContent);
    if (isEdit) {
      formData.append(
        "keepAttachments",
        JSON.stringify(existingAttachments.map((a) => a.fileName))
      );
    }
    newAttachments.forEach((file) => formData.append("attachments", file));

    setSaving(true);
    try {
      let res;
      if (isEdit) {
        res = await postReq(`/common/templates/${template._id}`, formData);
      } else {
        res = await postReq("/common/templates", formData);
      }

      if (res.status === 1) {
        message.success(res.message || "Template saved successfully");
        onClose();
        onSuccess();
      } else {
        message.error(res.message || "Failed to save template");
      }
    } catch (error) {
      console.error(error);
      message.error("An error occurred while saving the template");
    } finally {
      setSaving(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 KB";
    const kb = bytes / 1024;
    return kb < 1024 ? `${kb.toFixed(1)} KB` : `${(kb / 1024).toFixed(1)} MB`;
  };

  // Helper to check if body is empty (Quill empty state is usually <p><br></p>)
  const isBodyEmpty = !bodyContent || bodyContent.trim() === "<p><br></p>";

  return (
    <Modal
      open={open}
      title={null}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      width={1100}
      bodyStyle={{ padding: 0 }}
    >
      <Row style={{ minHeight: 550 }}>
        {/* =================================================================== */}
        {/* LEFT COLUMN: EDITOR FORM */}
        {/* =================================================================== */}
        <Col span={14} style={{ padding: 24, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
          <div>
            {/* Title Row */}
            <div style={{ display: "flex", alignItems: "center", border: "1px solid #d9d9d9", borderRadius: 4, padding: "6px 12px", marginBottom: 12 }}>
              <span style={{ color: "#8c8c8c", fontWeight: 500, width: 60 }}>Title</span>
              <div style={{ width: 1, height: 20, background: "#d9d9d9", margin: "0 12px" }} />
              <Input
                bordered={false}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Template Title"
                style={{ padding: 0, fontSize: 14 }}
              />
            </div>

            {/* Subject Row */}
            <div style={{ display: "flex", alignItems: "center", border: "1px solid #d9d9d9", borderRadius: 4, padding: "6px 12px", marginBottom: 16 }}>
              <span style={{ color: "#8c8c8c", fontWeight: 500, width: 60 }}>Subject</span>
              <div style={{ width: 1, height: 20, background: "#d9d9d9", margin: "0 12px" }} />
              <Input
                ref={subjectInputRef}
                bordered={false}
                value={subject}
                onChange={(e) => {
                  setSubject(e.target.value);
                  captureSubjectSelection(e);
                }}
                onSelect={captureSubjectSelection}
                onClick={captureSubjectSelection}
                onKeyUp={captureSubjectSelection}
                placeholder="Please enter subject..."
                style={{ padding: 0, fontSize: 14 }}
              />
              {/* Variable shortcut buttons on the right */}
              <Space size="small" style={{ marginLeft: "auto", color: "#8c8c8c", cursor: "pointer", fontSize: 12, fontWeight: 500 }}>
                {PLACEHOLDERS.map((p) => (
                  <Tooltip title={`Insert ${p.label}`} key={p.value}>
                    <span onMouseDown={(e) => e.preventDefault()} onClick={() => handleInsertPlaceholder(p.value, true)}>
                      {p.label}
                    </span>
                  </Tooltip>
                ))}
              </Space>
            </div>

            {/* Quill Editor Container */}
            <div style={{
              border: "1px solid #d9d9d9",
              borderRadius: 4,
              overflow: "hidden",
              borderColor: submitted && isBodyEmpty ? "#ff4d4f" : "#d9d9d9"
            }}>
              {/* Insert buttons in editor toolbar area (we can just float them next to toolbar or below it) */}
              <div style={{ display: "flex", justifyContent: "space-between", background: "#fafafa", borderBottom: "1px solid #d9d9d9", padding: "4px 12px", alignItems: "center" }}>
                <span style={{ fontSize: 12, color: "#8c8c8c" }}>Rich Text Body</span>
                <Space size="small" style={{ color: "#8c8c8c", cursor: "pointer", fontSize: 12, fontWeight: 500 }}>
                  {PLACEHOLDERS.map((p) => (
                    <Tooltip title={`Insert ${p.label}`} key={p.value}>
                      <span onMouseDown={(e) => e.preventDefault()} onClick={() => handleInsertPlaceholder(p.value)}>
                        {p.label}
                      </span>
                    </Tooltip>
                  ))}
                </Space>
              </div>
              <div ref={editorRef} style={{ minHeight: 300, border: "none", fontSize: 14 }} />
            </div>

            {/* Validation warning */}
            {submitted && isBodyEmpty && (
              <div style={{ color: "#ff4d4f", marginTop: 6, fontSize: 13 }}>
                ⚠️ Content should not be empty
              </div>
            )}

            {/* Attachments */}
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 6 }}>
                Attachments (sent with every campaign email using this template)
              </div>

              {(existingAttachments.length > 0 || newAttachments.length > 0) && (
                <List
                  size="small"
                  bordered
                  style={{ marginBottom: 8, background: "#fff" }}
                  dataSource={[
                    ...existingAttachments.map((a) => ({ ...a, kind: "existing" })),
                    ...newAttachments.map((f, idx) => ({
                      fileName: `new-${idx}`,
                      originalName: f.name,
                      size: f.size,
                      kind: "new",
                      idx,
                    })),
                  ]}
                  renderItem={(item) => (
                    <List.Item
                      style={{ padding: "6px 12px" }}
                      actions={[
                        <DeleteOutlined
                          key="remove"
                          style={{ color: "#ff4d4f", cursor: "pointer" }}
                          onClick={() => {
                            if (item.kind === "existing") {
                              setExistingAttachments((prev) =>
                                prev.filter((a) => a.fileName !== item.fileName)
                              );
                            } else {
                              setNewAttachments((prev) => prev.filter((_, i) => i !== item.idx));
                            }
                          }}
                        />,
                      ]}
                    >
                      <Space size="small">
                        <PaperClipOutlined style={{ color: "#8c8c8c" }} />
                        <Text style={{ fontSize: 13 }}>{item.originalName}</Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          ({formatFileSize(item.size)})
                        </Text>
                      </Space>
                    </List.Item>
                  )}
                />
              )}

              <Upload
                multiple
                showUploadList={false}
                beforeUpload={(file) => {
                  setNewAttachments((prev) => [...prev, file]);
                  return false; // prevent antd from auto-uploading; we send it ourselves on Save
                }}
              >
                <Button icon={<UploadOutlined />} size="small">
                  Attach Document
                </Button>
              </Upload>
            </div>
          </div>

          {/* Action buttons */}
          <div style={{ textAlign: "right", marginTop: 24 }}>
            <Space>
              <Button onClick={onClose}>Cancel</Button>
              <Button
                type="primary"
                loading={saving}
                onClick={handleSubmit}
                style={{ background: "#13487D", borderColor: "#13487D" }}
              >
                Save
              </Button>
            </Space>
          </div>
        </Col>

        {/* =================================================================== */}
        {/* RIGHT COLUMN: EMAIL PREVIEW */}
        {/* =================================================================== */}
        <Col span={10} style={{ padding: 24, background: "#f8f9fa", borderLeft: "1px solid #e8e8e8", display: "flex", flexDirection: "column" }}>
          {/* Preview Tab Header */}
          <div style={{ borderBottom: "2px solid #13487D", display: "inline-block", alignSelf: "flex-start", paddingBottom: 8, marginBottom: 20 }}>
            <Space style={{ color: "#13487D", fontWeight: 600 }}>
              <span role="img" aria-label="mail">✉️</span>
              <span>Email Preview</span>
            </Space>
          </div>

          {/* Live Preview Display */}
          {!subject.trim() && isBodyEmpty ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1, color: "#bfbfbf", textAlign: "center" }}>
              <span style={{ fontSize: 52, marginBottom: 16 }}>✉️</span>
              <Text type="secondary" style={{ color: "#a6a6a6" }}>Write something to generate the Email preview</Text>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
              {/* Subject Preview */}
              <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: "1px solid #e8e8e8" }}>
                <Text strong style={{ color: "#555" }}>Subject: </Text>
                <Text>{subject || <span style={{ color: "#bfbfbf", fontStyle: "italic" }}>No subject entered</span>}</Text>
              </div>

              {/* Body HTML Preview */}
              <div style={{
                flex: 1,
                padding: 16,
                background: "#ffffff",
                border: "1px solid #e8e8e8",
                borderRadius: 4,
                maxHeight: 420,
                overflowY: "auto"
              }}>
                {isBodyEmpty ? (
                  <span style={{ color: "#bfbfbf", fontStyle: "italic" }}>No body content entered</span>
                ) : (
                  <div className="ql-editor" style={{ padding: 0 }} dangerouslySetInnerHTML={{ __html: bodyContent }} />
                )}
              </div>
            </div>
          )}
        </Col>
      </Row>
    </Modal>
  );
}

// Utility import for date rendering

